import React from "react";
import { useSelector } from "react-redux";
import Langs, { Localize } from "../../../app/lang/langs";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";

/* ── Brand / palette constants ── */
const ORANGE = "#FF9015";
const ORANGE_GRADIENT = "linear-gradient(135deg, #FF8701, #FFC100)";
const ORANGE_LIGHT = "rgba(255,144,21,0.08)";
const ORANGE_BORDER = "rgba(255,144,21,0.22)";
const BORDER_SUBTLE = "rgba(0,0,0,0.06)";
const TEXT_PRIMARY = "#111318";
const TEXT_SECONDARY = "#4b5263";
const TEXT_MUTED = "#8b92a0";
const GREEN = "#16a34a";
const GREEN_BG = "rgba(22,163,74,0.07)";
const GREEN_BORDER = "rgba(22,163,74,0.22)";
const RED = "#dc2626";
const RED_BG = "rgba(220,38,38,0.07)";
const RED_BORDER = "rgba(220,38,38,0.22)";
const EASE = "cubic-bezier(0.4,0,0.2,1)";

/* CSS keyframe for entry animation */
const KEYFRAMES = `
    @keyframes quizResultsFadeIn {
        from { opacity: 0; transform: translateY(12px); }
        to   { opacity: 1; transform: translateY(0); }
    }
`;

/**
 * QuizResults
 * Displays the final results screen after a quiz attempt.
 * Shows score, pass/fail status, statistics, and action buttons.
 * Allows unlimited immediate retakes.
 *
 * @param {object}   props
 * @param {object}   props.results   - { score, passed, total_questions, correct_answers, passing_score }
 * @param {object}   props.quiz      - Quiz meta-data including title
 * @param {Function} props.onRetake  - Callback to restart the quiz from the beginning
 * @param {Function} props.onExit    - Callback to exit back to the pending quiz list
 */
export default function QuizResults({ results, quiz, onRetake, onExit }) {
    const lang = useSelector((state) => state.settings.lang);
    const isMobileRedux = useSelector((state) => state.settings.isMobile);

    const theme = useTheme();
    const isSmallQuery = useMediaQuery(theme.breakpoints.down("sm"));
    const isSmall = isMobileRedux === 2 || isSmallQuery;

    /* Guard: render a minimal fallback if results are not yet available */
    if (!results) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
                <CircularProgress sx={{ color: ORANGE }} />
            </Box>
        );
    }
    const { score, passed, total_questions, correct_answers, passing_score } = results;
    const allowScores = Boolean(quiz?.allow_scores);
    const allowMultipleTakes = Boolean(quiz?.allow_multiple_takes);

    /* Clamp score to [0, 100] for the circular progress display */
    const clampedScore = Math.min(100, Math.max(0, score ?? 0));
    const scoreColor = passed ? GREEN : RED;

    /* Responsive button styles */
    const primaryBtnSx = {
        ...primaryButtonSx,
        minHeight: 48,
        ...(isSmall ? { width: "100%", fontSize: "0.95rem" } : {}),
    };
    const outlinedBtnSx = {
        ...outlinedButtonSx,
        minHeight: 48,
        ...(isSmall ? { width: "100%", fontSize: "0.95rem" } : {}),
    };

    return (
        <>
            <style>{KEYFRAMES}</style>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: isSmall ? 2.5 : 3,
                    animation: `quizResultsFadeIn 450ms ${EASE} both`,
                    px: isSmall ? 0.5 : 0,
                }}
            >
                {allowScores ? (
                    <>
                        {/* ── Score hero section ── */}
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 2,
                                py: isSmall ? 2 : 3,
                            }}
                        >
                            {/* Circular score ring */}
                            <Box sx={{ position: "relative", display: "inline-flex" }}>
                                <CircularProgress
                                    variant="determinate"
                                    value={100}
                                    size={isSmall ? 110 : 128}
                                    thickness={4.5}
                                    sx={{ color: "rgba(0,0,0,0.06)", position: "absolute" }}
                                />
                                <CircularProgress
                                    variant="determinate"
                                    value={clampedScore}
                                    size={isSmall ? 110 : 128}
                                    thickness={4.5}
                                    sx={{ color: scoreColor }}
                                />
                                <Box
                                    sx={{
                                        position: "absolute",
                                        inset: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: isSmall ? "1.4rem" : "1.65rem",
                                            fontWeight: 800,
                                            color: scoreColor,
                                            fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                                            letterSpacing: "-0.03em",
                                            lineHeight: 1,
                                        }}
                                    >
                                        {clampedScore}%
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Pass / Fail label */}
                            <Stack direction="row" alignItems="center" spacing={1}>
                                {passed ? (
                                    <CheckCircleIcon sx={{ color: GREEN, fontSize: isSmall ? 24 : 28 }} />
                                ) : (
                                    <CancelIcon sx={{ color: RED, fontSize: isSmall ? 24 : 28 }} />
                                )}
                                <Typography
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: isSmall ? "1.25rem" : "1.45rem",
                                        color: passed ? GREEN : RED,
                                        fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                                        letterSpacing: "-0.03em",
                                    }}
                                >
                                    {passed ? Localize("VSEC_RESULTS_PASSED", lang) : Localize("VSEC_RESULTS_FAILED", lang)}
                                </Typography>
                            </Stack>

                            {quiz?.title && (
                                <Typography
                                    sx={{
                                        fontSize: "0.9rem",
                                        color: TEXT_MUTED,
                                        fontFamily: "'Inter',sans-serif",
                                        textAlign: "center",
                                        px: isSmall ? 2 : 0,
                                    }}
                                >
                                    {quiz.title}
                                </Typography>
                            )}
                        </Box>

                        {/* ── Statistics row ── */}
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                            <StatCard
                                label={Localize("VSEC_RESULTS_STAT_CORRECT", lang)}
                                value={`${correct_answers ?? 0} / ${total_questions ?? 0}`}
                                color={TEXT_PRIMARY}
                            />
                            <StatCard
                                label={Localize("VSEC_RESULTS_STAT_MIN_SCORE", lang)}
                                value={`${passing_score ?? 0}%`}
                                color={TEXT_MUTED}
                            />
                        </Box>

                        {/* ── Pass / Fail contextual card ── */}
                        {passed ? (
                            <Card elevation={0} sx={{ borderRadius: isSmall ? "12px" : "16px", background: GREEN_BG, border: `1px solid ${GREEN_BORDER}` }}>
                                <CardContent sx={{ p: isSmall ? "14px 16px !important" : "18px 20px !important" }}>
                                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                        <EmojiEventsOutlinedIcon sx={{ color: GREEN, fontSize: 22, mt: 0.15, flexShrink: 0 }} />
                                        <Typography sx={{ fontSize: isSmall ? "0.88rem" : "0.92rem", color: GREEN, fontWeight: 600, fontFamily: "'Inter',sans-serif", lineHeight: 1.55 }}>
                                            {Localize("VSEC_RESULTS_CONGRATS", lang)}
                                        </Typography>
                                    </Stack>
                                </CardContent>
                            </Card>
                        ) : (
                            <Card elevation={0} sx={{ borderRadius: isSmall ? "12px" : "16px", background: ORANGE_LIGHT, border: `1px solid ${ORANGE_BORDER}` }}>
                                <CardContent sx={{ p: isSmall ? "14px 16px !important" : "18px 20px !important" }}>
                                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                        <WarningAmberRoundedIcon sx={{ color: ORANGE, fontSize: 22, mt: 0.15, flexShrink: 0 }} />
                                        <Typography sx={{ fontSize: isSmall ? "0.88rem" : "0.92rem", color: TEXT_SECONDARY, fontFamily: "'Inter',sans-serif", lineHeight: 1.55 }}>
                                            {Localize("VSEC_RESULTS_FAIL_MSG", lang)}{" "}
                                            <strong style={{ color: TEXT_PRIMARY }}>{passing_score ?? 0}%</strong>{" "}
                                            {Localize("VSEC_RESULTS_FAIL_MSG2", lang)}
                                        </Typography>
                                    </Stack>
                                </CardContent>
                            </Card>
                        )}
                    </>
                ) : (
                    /* ── Scores hidden: show "answers recorded" confirmation ── */
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                            py: isSmall ? 3 : 5,
                        }}
                    >
                        <Box
                            sx={{
                                width: isSmall ? 64 : 80,
                                height: isSmall ? 64 : 80,
                                borderRadius: isSmall ? "18px" : "22px",
                                background: "rgba(22,163,74,0.08)",
                                border: "1px solid rgba(22,163,74,0.18)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <TaskAltRoundedIcon sx={{ fontSize: isSmall ? 30 : 38, color: GREEN }} />
                        </Box>
                        <Typography
                            sx={{
                                fontWeight: 750,
                                fontSize: isSmall ? "1.1rem" : "1.25rem",
                                color: TEXT_PRIMARY,
                                fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                                letterSpacing: "-0.02em",
                                textAlign: "center",
                                px: isSmall ? 2 : 0,
                            }}
                        >
                            {Localize("VSEC_RESULTS_ANSWERS_RECORDED", lang)}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "0.88rem",
                                color: TEXT_MUTED,
                                fontFamily: "'Inter',sans-serif",
                                textAlign: "center",
                                px: isSmall ? 2 : 0,
                            }}
                        >
                            {Localize("VSEC_RESULTS_ANSWERS_RECORDED_SUB", lang)}
                        </Typography>
                    </Box>
                )}

                {/* ── Action buttons ── */}
                <Stack
                    direction={isSmall ? "column" : { xs: "column", sm: "row" }}
                    spacing={1.5}
                    justifyContent="flex-end"
                    sx={{
                        pt: 0.5,
                        pb: isSmall ? "calc(8px + env(safe-area-inset-bottom, 0px))" : 0,
                    }}
                >
                    {!allowScores || passed || !allowMultipleTakes ? (
                        <Button
                            variant="contained"
                            onClick={onExit}
                            startIcon={<ArrowBackIcon sx={{ fontSize: "18px !important" }} />}
                            sx={primaryBtnSx}
                        >
                            {Localize("VSEC_BTN_BACK_TO_QUIZZES", lang)}
                        </Button>
                    ) : (
                        <>
                            <Button
                                variant="outlined"
                                onClick={onExit}
                                startIcon={<ArrowBackIcon sx={{ fontSize: "18px !important" }} />}
                                sx={outlinedBtnSx}
                            >
                                {Localize("VSEC_BTN_BACK_TO_QUIZZES", lang)}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={onRetake}
                                startIcon={<RefreshIcon sx={{ fontSize: "18px !important" }} />}
                                sx={primaryBtnSx}
                            >
                                {Localize("VSEC_BTN_RETRY_NOW", lang)}
                            </Button>
                        </>
                    )}
                </Stack>

            </Box>
        </>
    );
}

/* ── Shared button styles ── */

const primaryButtonSx = {
    borderRadius: "12px",
    px: 3,
    py: 1.1,
    textTransform: "none",
    fontWeight: 650,
    fontSize: "0.93rem",
    letterSpacing: "-0.01em",
    fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
    background: ORANGE_GRADIENT,
    color: "#fff",
    boxShadow: `0 4px 14px rgba(255,144,21,0.26), 0 1px 4px rgba(255,144,21,0.14)`,
    transition: `all 280ms ${EASE}`,
    "&:hover": {
        background: ORANGE_GRADIENT,
        boxShadow: `0 6px 20px rgba(255,144,21,0.36), 0 2px 8px rgba(255,144,21,0.18)`,
        transform: "translateY(-1px)",
    },
    "&:active": { transform: "scale(0.98)" },
};

const outlinedButtonSx = {
    borderRadius: "12px",
    px: 3,
    py: 1.1,
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.93rem",
    letterSpacing: "-0.01em",
    fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
    color: TEXT_SECONDARY,
    borderColor: BORDER_SUBTLE,
    transition: `all 250ms ${EASE}`,
    "&:hover": {
        borderColor: "rgba(0,0,0,0.18)",
        background: "rgba(0,0,0,0.03)",
    },
};

/* ── Internal sub-components ── */

function CorrectionsCard({ index, question, answerResult, lang }) {
    const isCorrect = answerResult?.is_correct;
    const options   = answerResult?.options ?? [];
    const explanation = answerResult?.explanation;

    const headerBg     = isCorrect ? GREEN_BG    : RED_BG;
    const headerBorder = isCorrect ? GREEN_BORDER : RED_BORDER;
    const headerColor  = isCorrect ? GREEN         : RED;
    const HeaderIcon   = isCorrect ? CheckCircleIcon : CancelIcon;

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: "14px",
                border: `1px solid ${BORDER_SUBTLE}`,
                overflow: "hidden",
            }}
        >
            {/* Question header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2.5,
                    py: 1.5,
                    background: headerBg,
                    borderBottom: `1px solid ${headerBorder}`,
                }}
            >
                <HeaderIcon sx={{ color: headerColor, fontSize: 18, flexShrink: 0 }} />
                <Typography
                    sx={{
                        fontWeight: 650,
                        fontSize: "0.88rem",
                        color: TEXT_SECONDARY,
                        fontFamily: "'Inter',sans-serif",
                        flex: 1,
                    }}
                >
                    <Box component="span" sx={{ color: TEXT_MUTED, mr: 1 }}>
                        Q{index}.
                    </Box>
                    {question.question_text || question.text || question.content}
                </Typography>
            </Box>

            {/* Options list */}
            <Stack spacing={0.75} sx={{ px: 2.5, py: 2 }}>
                {options.map((opt) => {
                    const correct     = Boolean(opt.is_correct);
                    const selected    = Boolean(opt.was_selected);

                    let bg, border, color, Icon;
                    if (correct && selected) {
                        bg = GREEN_BG; border = GREEN_BORDER; color = GREEN;
                        Icon = CheckCircleIcon;
                    } else if (correct && !selected) {
                        bg = GREEN_BG; border = GREEN_BORDER; color = GREEN;
                        Icon = CheckCircleOutlineIcon;
                    } else if (!correct && selected) {
                        bg = RED_BG; border = RED_BORDER; color = RED;
                        Icon = CancelIcon;
                    } else {
                        bg = "transparent"; border = BORDER_SUBTLE; color = TEXT_MUTED;
                        Icon = RadioButtonUncheckedIcon;
                    }

                    return (
                        <Box
                            key={opt.id}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.25,
                                px: 1.5,
                                py: 0.9,
                                borderRadius: "10px",
                                background: bg,
                                border: `1px solid ${border}`,
                            }}
                        >
                            <Icon sx={{ color, fontSize: 17, flexShrink: 0 }} />
                            <Typography
                                sx={{
                                    fontSize: "0.85rem",
                                    color: correct ? (selected ? GREEN : GREEN) : (selected ? RED : TEXT_MUTED),
                                    fontFamily: "'Inter',sans-serif",
                                    fontWeight: correct ? 600 : 400,
                                }}
                            >
                                {opt.option_text}
                            </Typography>
                        </Box>
                    );
                })}
            </Stack>

            {/* Explanation */}
            {explanation && (
                <Box
                    sx={{
                        mx: 2.5,
                        mb: 2,
                        px: 2,
                        py: 1.5,
                        borderRadius: "10px",
                        background: ORANGE_LIGHT,
                        border: `1px solid ${ORANGE_BORDER}`,
                        display: "flex",
                        gap: 1.25,
                        alignItems: "flex-start",
                    }}
                >
                    <LightbulbOutlinedIcon sx={{ color: ORANGE, fontSize: 17, mt: 0.2, flexShrink: 0 }} />
                    <Typography
                        sx={{
                            fontSize: "0.83rem",
                            color: TEXT_SECONDARY,
                            fontFamily: "'Inter',sans-serif",
                            lineHeight: 1.6,
                        }}
                    >
                        <Box component="span" sx={{ fontWeight: 650, color: TEXT_PRIMARY, mr: 0.5 }}>
                            {Localize("VSEC_EXPLANATION_LABEL", lang)} :
                        </Box>
                        {explanation}
                    </Typography>
                </Box>
            )}
        </Card>
    );
}

/**
 * StatCard
 * A small stats tile used in the result summary grid.
 */
function StatCard({ label, value, color }) {
    return (
        <Box
            sx={{
                borderRadius: "14px",
                background: "#fff",
                border: `1px solid ${BORDER_SUBTLE}`,
                px: 2.5,
                py: 2,
                display: "flex",
                flexDirection: "column",
                gap: 0.4,
                textAlign: "center",
            }}
        >
            <Typography
                sx={{
                    fontWeight: 750,
                    fontSize: "1.3rem",
                    color,
                    fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                    letterSpacing: "-0.02em",
                    lineHeight: 1,
                }}
            >
                {value}
            </Typography>
            <Typography
                sx={{
                    fontSize: "0.75rem",
                    color: TEXT_MUTED,
                    fontFamily: "'Inter',sans-serif",
                    fontWeight: 500,
                }}
            >
                {label}
            </Typography>
        </Box>
    );
}
